import { ActionIcon, Container, Flex, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconCheck, IconEdit, IconKey, IconPlus, IconTrash, IconX } from '@tabler/icons-react'
import { useDeleteUser } from 'hooks/users'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo } from 'react'
import { Role, RoleName } from 'types/auth'
import { User } from 'types/users'

type Props = {
  data: Array<User>
}

export default function UsersTable({ data }: Props) {
  const deleteUser = useDeleteUser()

  function openAddUserModal() {
    modals.openContextModal({
      modal: 'addUser',
      centered: true,
      title: 'Dodaj użytkownika',
      innerProps: {},
    })
  }

  function openUpdatePasswordModal(userId: string) {
    modals.openContextModal({
      modal: 'updatePassword',
      centered: true,
      title: 'Zmień hasło',
      innerProps: {
        userId,
      },
    })
  }

  function openUpdateUserModal(user: User) {
    modals.openContextModal({
      modal: 'updateUser',
      centered: true,
      title: 'Zaktualizuj dane użytkownika',
      innerProps: {
        user,
      },
    })
  }

  function openDeleteUserModal(user: User) {
    modals.openConfirmModal({
      title: 'Usuń użytkownika',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć użytkownika{' '}
          <b>
            {user.firstName} {user.lastName}
          </b>
          ? Nie będzie można cofnąć tej operacji. Wszystkie dane użytkownika (docięte lub uszyte metry, historia)
          zostaną usunięte. Jeśli chcesz zachować dane tego użytkownika, zmień jego status na nieaktywny.
        </Text>
      ),
      labels: { confirm: 'Usuń użytkownika', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteUser.mutateAsync(user.id),
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<User>>>(
    () => [
      {
        accessorKey: 'firstName',
        header: 'Imię',
      },
      {
        accessorKey: 'lastName',
        header: 'Nazwisko',
      },
      {
        accessorKey: 'username',
        header: 'Nazwa użytkownika',
      },
      {
        accessorKey: 'roles',
        header: 'Administrator',
        Cell: ({ cell }) => {
          const roles = cell.getValue<Array<Role>>()
          return roles.some((r) => r.name === RoleName.ADMIN) ? (
            <IconCheck stroke={2.5} color="green" size={22} />
          ) : null
        },
      },
      {
        accessorKey: 'active',
        header: 'Aktywne',
        Cell: ({ cell }) => {
          return cell.getValue() ? (
            <IconCheck stroke={2.5} color="green" size={22} />
          ) : (
            <IconX stroke={2.5} color="red" size={22} />
          )
        },
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns,
    data,
    layoutMode: 'grid',
    enableColumnActions: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableGlobalFilter: false,
    enableBottomToolbar: data.length < 5 ? false : true,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    pageCount: 5,
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 140,
      },
    },
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        <Tooltip label="Dodaj użytkownika">
          <ActionIcon size="lg" variant="subtle" onClick={openAddUserModal} ml="auto">
            <IconPlus />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Edytuj">
          <ActionIcon variant="light" color="gray" radius="xl" onClick={() => openUpdateUserModal(row.original)}>
            <IconEdit size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Zmień hasło">
          <ActionIcon variant="light" color="blue" radius="xl" onClick={() => openUpdatePasswordModal(row.original.id)}>
            <IconKey size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Usuń">
          <ActionIcon radius="xl" variant="light" color="red" onClick={() => openDeleteUserModal(row.original)}>
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>
      </Flex>
    ),
  })
  return <MantineReactTable table={table} />
}
